import {ActionReducer, createFeature, createReducer, createSelector, on} from '@ngrx/store';
import {EntityState, EntityAdapter, createEntityAdapter, Dictionary} from '@ngrx/entity';
import {Update} from "@ngrx/entity";
import {FulfillmentActions, FulfillmentAPiActions, FulfillmentChannelActions} from "./fulfillment.action";
import {PaginationModel} from "../../../../commons/angular/models/pagination.model";
import {FulfillmentModel} from "../../../models/fulfillment.model";
import {FulfillmentStepModel} from "../../../models/fulfillment-step.model";

const getFulfillmentStepArr = (oldFulfillmentSteps: FulfillmentStepModel[], newFulfillmentSteps: FulfillmentStepModel[]) => {
  let fulfillmentSteps = [...oldFulfillmentSteps];
  newFulfillmentSteps.forEach(nfs => {
    const oldFsIndex = (fulfillmentSteps || []).findIndex(ofs => ofs.id === nfs.id);
    if (oldFsIndex > -1) {
      fulfillmentSteps[oldFsIndex] = nfs;
    } else {
      fulfillmentSteps.push(nfs);
    }
  })

  return fulfillmentSteps.filter(fs => fs.status !== 'Splitted');
}

export interface FulfillmentState extends EntityState<FulfillmentModel> {
  pagination: PaginationModel;
  isLoading: boolean;
  initialized: boolean;
}

const adapter: EntityAdapter<FulfillmentModel> = createEntityAdapter<FulfillmentModel>();

const initialState: FulfillmentState = adapter.getInitialState({
  pagination: null,
  isLoading: false,
  initialized: false
});

const reducer: ActionReducer<FulfillmentState> = createReducer(
  initialState,

  on(FulfillmentChannelActions.notificationReceived, (state, {notification}) => {

    if (state.entities[notification.id]?.updatedAt < notification.updatedAt) {
      const changes: Update<FulfillmentModel> = {
        id: notification.id,
        changes: {
          ...notification,
          fulfillmentSteps: getFulfillmentStepArr(state.entities[notification.id]?.fulfillmentSteps || [], notification.fulfillmentSteps || [])
        }
      }
      return adapter.updateOne(changes, state);
    } else {
      return state;
    }

  }),

  on(FulfillmentAPiActions.getFulfillmentsPaginatedResult, (state) => ({...state, isLoading: true})),

  on(FulfillmentAPiActions.getFulfillmentsPaginatedResultSuccess, (state, {paginatedResult}) => {
    return adapter.setMany(paginatedResult.content, {
      ...state,
      pagination: paginatedResult.pagination,
      isLoading: false,
      initialized: true
    })
  }),

  on(FulfillmentAPiActions.getFulfillmentsPaginatedResultFailure, (state) => ({...state, isLoading: false})),

  on(FulfillmentAPiActions.getFulfillment, (state) => ({...state, isLoading: true})),

  on(FulfillmentAPiActions.getFulfillmentSuccess, (state, {fulfillment}) => {
    return adapter.setOne(fulfillment, {...state, isLoading: false, initialized: true})
  }),

  on(FulfillmentAPiActions.getFulfillmentFailure, (state) => ({...state, isLoading: false})),

  on(FulfillmentActions.removeAll, (state) => adapter.removeAll({...state, pagination: null, initialized: false}))
);


export const fulfillmentFeature = createFeature({
  name: 'Fulfillment',
  reducer,
  extraSelectors: ({selectFulfillmentState, selectEntities, selectPagination, selectIsLoading, selectInitialized}) => ({
    ...adapter.getSelectors(selectFulfillmentState),
    selectFulfillment: (id: string) => createSelector(
      selectEntities,
      (entities: Dictionary<FulfillmentModel>) => entities[id],
    ),
    selectPaginatedResult: () => createSelector(
      selectPagination,
      selectEntities,
      (pagination: PaginationModel, entities: Dictionary<FulfillmentModel>) => {
        return {
          pagination: pagination,
          fulfillments: Object.values(entities)
        }
      }
    ),
    selectIsLoading: () => createSelector(
      selectIsLoading,
      (isLoading) => isLoading
    ),
    selectInitialized: () => createSelector(
      selectInitialized,
      (initialized) => initialized
    )
  })
});
